<template>
  <div class="content-wrapper">
    <h2 class="page-titles">View Details</h2>
    <div class="nav">
      <div class="arrow-link" @click="$router.back()">
        <router-link to>
          <span>Back</span>
        </router-link>
      </div>
    </div>

    <div class="text-left my-5">
      <Skeleton v-if="loadingOrder" width="20%" height="2rem" />
      <span class="font-normal editorial" v-else-if="orderDetails"
        >Order # {{ orderDetails.id }}</span
      >
    </div>

    <Skeleton v-if="loadingOrder" width="100%" height="12rem" />
    <div class="p-5 px-7 address-holder" v-else-if="orderDetails">
      <div class="flex mb-5 w-full justify-between">
        <div class="w-1/2 text-left font-bold">
          Shipping Address
        </div>
        <div
          class="text-right place-self-end"
          v-if="orderDetails.attributes.trackingNumber"
        >
          <p class="flex gap-5">
            <strong>Tracking Number</strong>
            <span class="text-sm">
              {{ orderDetails.attributes.trackingNumber }}</span
            >
          </p>
        </div>
      </div>

      <div class="flex">
        <div class="w-1/2">
          <div class="mb-5 text-left">
            <p class="text-sm">
              {{ orderDetails.attributes.recipientDetails.name }}
            </p>
            <p class="text-sm">
              {{ orderDetails.attributes.recipientDetails.email }}
            </p>
          </div>

          <div class="mb-5 text-left">
            <p class="text-sm">
              {{ orderDetails.attributes.recipientDetails.address_one }}
            </p>
            <p class="text-sm">
              {{ orderDetails.attributes.recipientDetails.address_two }}
            </p>
            <p class="text-sm">
              {{ orderDetails.attributes.recipientDetails.city }},
              {{ orderDetails.attributes.recipientDetails.state }}
              {{ orderDetails.attributes.recipientDetails.zipcode }}
            </p>
          </div>
        </div>
        <div class="w-1/2 text-right" v-if="false">
          <img src="../../assets/adams-point.png" class="w-36 ml-auto" />
        </div>
      </div>
    </div>

    <div class="ordered-items mt-10">
      <div v-if="loadingOrder">
        <Skeleton v-for="i in 3" width="100%" height="3rem" :key="i" />
      </div>
      <table v-else-if="orderDetails" class="ordered-items-table w-full">
        <thead>
          <tr>
            <th class="w-1/6 text-center">Product</th>
            <th class="w-2/6 text-left">Product Name</th>
            <th class="w-1/6">Option</th>
            <th class="w-1/6">Quantity</th>
            <th class="w-1/6">Subtotal</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, i) in orderDetails.attributes.cartItems" :key="i">
            <td>
              <div class="product-image flex items-center h-16  text-center">
                <img
                  :src="getItemImage(item.productVariantAttributes.images)"
                  class="mx-auto text-center object-contain"
                  @error="handleBrokenImage($event)"
                  alt=""
                />
              </div>
            </td>
            <td>
              {{ item.productVariantAttributes.display_name }}
            </td>
            <td>{{ item.productVariantAttributes.name }}</td>
            <td>{{ item.quantity }}</td>
            <td>{{ formatCurrency(item.price * item.quantity) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="payment-summary mt-5" v-if="orderDetails">
      <div class="md:w-1/3 w-3/4 ml-auto">
        <div class="flex w-full text-sm py-1">
          <div class="w-2/4 text-right">Subtotal</div>
          <div class="w-2/4">
            {{ formatCurrency(orderDetails.attributes.subTotal) }}
          </div>
        </div>
        <div class="flex w-full text-sm py-1">
          <div class="w-2/4 text-right">Shipping</div>
          <div class="w-2/4">
            {{
              formatCurrency(
                orderDetails.attributes.costingDetails.charged_shipping_fee
              )
            }}
          </div>
        </div>
        <div class="flex w-full text-sm py-1">
          <div class="w-2/4 text-right">Sales Tax</div>
          <div class="w-2/4">
            {{ formatCurrency(orderDetails.attributes.costingDetails.tax) }}
          </div>
        </div>

        <div class="flex w-full text-sm py-1 pb-6 is-bordered">
          <div class="w-2/4 text-right font-bold">Customer Total</div>
          <div class="customer-total-holder flex items-center font-bold">
            {{
              formatCurrency(
                orderDetails.attributes.costingDetails.customer_total_amount
              )
            }}
            <!-- Tooltip Here -->
            <div class="tooltip-holder">
              <Tooltip
                :popOverContent="tooltipContents.customerTotal"
                :popOverPosition="tooltipPosition"
                icon="i"
              />
            </div>
          </div>
        </div>

        <section class="payout-region" :class="{ hidden: !isOpen }">
          <div class="flex w-full text-sm py-1 pt-4">
            <div class="w-2/4 text-right">Fulfillment Fee</div>
            <div class="w-2/4">
              -{{
                formatCurrency(
                  orderDetails.attributes.costingDetails.fulfillment_fee
                )
              }}
            </div>
          </div>

          <div class="flex w-full text-sm py-1">
            <div class="w-2/4 text-right">Credit Card Fee</div>
            <div class="w-2/4">
              -{{
                formatCurrency(
                  orderDetails.attributes.costingDetails.credit_card_fee
                )
              }}
            </div>
          </div>

          <div class="flex w-full text-sm py-1">
            <div class="w-2/4 text-right">Shipment Fee</div>
            <div class="shipping-fee-holder flex items-center">
              {{
                orderDetails.attributes.costingDetails.shipping_fee == 'TBD'
                  ? 'TBD'
                  : '-' +
                    formatCurrency(
                      orderDetails.attributes.costingDetails.shipping_fee
                    )
              }}

              <!-- Tooltip Here -->
              <div class="tooltip-holder">
                <Tooltip
                  :popOverContent="tooltipContents.shippingFee"
                  :popOverPosition="tooltipPosition"
                  icon="i"
                />
              </div>
            </div>
          </div>
        </section>

        <div
          class="flex w-full text-sm py-1 is-bordered"
          :class="{ 'pt-3 py-0 border-0': !isOpen }"
        >
          <div class="w-2/4 text-right font-bold pb-3">Net Payout</div>
          <div class="net-payout-holder font-bold">
            {{
              orderDetails.attributes.costingDetails.net_payout_amount == 'TBD'
                ? 'TBD'
                : formatCurrency(
                    orderDetails.attributes.costingDetails.net_payout_amount
                  )
            }}

            <svg
              @click="isOpen = !isOpen"
              :class="{ closed: !isOpen }"
              width="9"
              height="5"
              viewBox="0 0 9 5"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M4.5 0C4.61234 0 4.72469 0.043539 4.81026 0.130173L8.87131 4.24151C9.0429 4.41522 9.0429 4.696 8.87131 4.86972C8.69972 5.04343 8.42237 5.04343 8.25079 4.86972L4.5 1.07248L0.749214 4.86972C0.577626 5.04343 0.300278 5.04343 0.128691 4.86972C-0.0428972 4.696 -0.0428972 4.41522 0.128691 4.24151L4.18974 0.130173C4.27531 0.043539 4.38766 0 4.5 0Z"
                fill="#25282A"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Skeleton from '../Universal/Skeleton'
import Util from '../../mixins/Util'
import { defaultNoTrendingImage, handleBrokenImage } from '../../helpers'
import Tooltip from '@/components/Universal/ToolTipInfo.vue'

export default {
  name: 'EcommerceOrderDetails',
  components: {
    Skeleton,
    Tooltip
  },
  mixins: [Util],

  data: () => ({
    isOpen: true,
    handleBrokenImage: handleBrokenImage,
    tooltipContents: {
      shippingFee:
        'Shipping fee will be calculated when order has been shipped.',
      customerTotal:
        'Includes Fullfillment fee, Credit Card fee and Shipment fee.'
    }
  }),

  computed: {
    ...mapState({
      orderDetails: state => state.ecommerce.orderDetails,
      loadingOrder: state => state.ecommerce.loadingOrder
    }),

    tooltipPosition() {
      if (this.innerWidth < 1000) {
        return 'bottom-start'
      }
      return 'right'
    }
  },

  created() {
    this.$store.dispatch('ecommerce/getOrderDetails', {
      order_id: this.$route.params.id
    })
  },

  methods: {
    getItemImage(itemImages) {
      return itemImages === null
        ? defaultNoTrendingImage
        : itemImages.length
        ? itemImages[0].url
        : defaultNoTrendingImage
    }
  }
}
</script>

<style scoped lang="scss">
.product-image {
  img {
    height: inherit;
  }
}
.arrow-link {
  margin-top: 4rem;
  margin-bottom: 1.5rem;
}

.address-holder {
  border: 1px solid #232220;
}
.ordered-items-table {
  thead {
    th {
      font-size: 0.875rem;
      padding: 10px 0;
    }
  }

  tr {
    border-bottom: 1px solid #979797;
  }

  tbody td {
    font-size: 0.813rem;
    padding: 10px 0;
  }
}

.is-bordered {
  border-bottom: 1px solid #979797;
}

.shipping-fee-holder,
.customer-total-holder {
  width: 30%;
  margin-left: auto;

  .tooltip-holder {
    position: relative;
    margin-left: 7px;
    bottom: 2px;
  }
}

.net-payout-holder {
  margin-left: auto;
  display: flex;
  align-items: baseline;
  width: 30%;

  svg {
    margin-left: 4px;
    cursor: pointer;
    transition: ease-in 0.3s;

    &.closed {
      transform: rotate(180deg);
    }
  }
}
</style>

<style lang="scss">
.shipping-fee-holder,
.customer-total-holder {
  .popover-inner {
    background: #dfdad2;
    border: none;
  }
}
</style>
